import { getClient } from '@api/client';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import appManager from '@app/appManager';
import IAppEvents from '@app/lib/IAppEvents';
import IAppLogger from '@app/lib/IAppLogger';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import { mapToUserSetAvailabilityStatus } from '@lib/utils/userAvailabilityActivityStatus';
import AuthModule from '@modules/AuthModule';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import FullStoryModule from '@modules/FullStoryModule';
import ModalsModule from '@modules/ModalsModule';
import { ModalId } from '@modules/ModalsModule/types';
import ServiceModule from '@modules/ServiceModule';
import {
  ServiceSessionEventType,
  ServiceSessionRecoveryEvent,
} from '@modules/ServiceModule/workspace/types/ServiceEvents';
import { webphoneAppEvents } from '@modules/SoftPhoneModule/constants/events';
import type { NetworkTestResultPayload } from '@modules/SoftPhoneModule/types';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import ToastModule from '@modules/ToastModule';
import { userStatusState } from '@modules/UserStatusModule/state';
import { UserStatusEventType } from '@modules/UserStatusModule/types';
import { hasValidSeat } from 'src/constants/user';
import AppModule from '../../app/lib/AppModule';
import {
  CANCEL_WRAPUP_FAILED,
  UPDATE_STATUS_FAILED,
} from '../../constants/errorCodes';
import { FORCE_AWAY_TOAST_DURATION } from './constants/forceAwayToastDuration';
import strings from './constants/strings';
import getCurrentUserStatus from './data/getCurrentUserStatusQuery';
import refreshUserStatusMutation from './data/refreshUserStatusMutation';
import { StuckStatusDetector } from './services/StuckStatusDetector';
import { isApolloError } from '@api/lib/apolloError';

export class UserStatusModule extends AppModule {
  override events = [
    UserStatusEventType.USER_STATUS_UPDATED_AVAILABLE,
    UserStatusEventType.USER_STATUS_STUCK,
    webphoneAppEvents.networkTestProgress,
    webphoneAppEvents.networkTestResult,
  ];

  private stuckStatusDetector: StuckStatusDetector;

  private networkTestPreviousAvailabilityStatus: UserSetAvailabilityStatus | null =
    null;
  private networkTestUserId: string | null = null;
  private networkTestUnavailableUpdate: Promise<void> | null = null;

  constructor(events: IAppEvents, logger: IAppLogger) {
    super(events, logger);
    this.stuckStatusDetector = new StuckStatusDetector(events, logger);
  }

  override preload(): void {
    this.app.events.on('user.status.force_away', () => {
      ToastModule.showToast({
        title: strings.toasts.statusChangedTitle,
        status: 'info',
        duration: FORCE_AWAY_TOAST_DURATION,
        toastBody: strings.toasts.statusChangedBody,
      });
    });

    this.app.events.on(AuthEvents.SIGNIN, (user: AuthUser) => {
      if (!ServiceModule.getCurrentUserId()) {
        ServiceModule.setCurrentUserId(user.id);
      }
      // register signout handler to set our status to offline
      // when we're signing out
      AuthModule.registerSignoutHandler('userstatus', async () => {
        // Only set status to offline if user has a seat
        const hasSeat = hasValidSeat(user?.seatLevel);
        if (hasSeat) {
          await this.updateUserStatus(
            user.id,
            UserSetAvailabilityStatus.Offline,
          );
        }
      });
    });

    this.app.events.on('featureflags.initialized', () => {
      // Register refresh listener if feature is enabled
      if (
        FeatureFlagModule.isEnabled(
          FeatureFlagKeys.featureAutoRefreshUserStatus,
        )
      ) {
        // listen to the user status refresh event to refresh user status
        this.app.events.on(
          UserStatusEventType.USER_STATUS_STUCK,
          this.handleStuckStatus.bind(this),
        );
      }

      // Start stuck status detection
      this.stuckStatusDetector.start();
    });

    this.app.events.on(AuthEvents.SIGNOUT, () => {
      ServiceModule.setCurrentUserId(null);

      // unregister handlers for signout
      AuthModule.unregisterCanSignoutHandler('userstatus');

      // Stop stuck status detection
      this.stuckStatusDetector.stop();
      this.clearNetworkTestAvailabilityState();
    });

    // listen to the wrap up started event to set the wrap up time and disable status change
    this.app.events.on<ServiceSessionRecoveryEvent>(
      ServiceSessionEventType.RECOVERY_STARTED,
      (event) => {
        this.setRecoveryState(
          event.userId,
          new Date(event.timestamp).getTime() + event.recoveryAllowedMs,
        );
      },
    );

    // listen to the wrap up completed event to enable status change
    this.app.events.on<ServiceSessionRecoveryEvent>(
      ServiceSessionEventType.RECOVERY_COMPLETED,
      (event: any) => {
        this.setRecoveryState(event.userId, null);
      },
    );

    // Webphone network quality test: unavailable while test runs; restore prior status when it succeeds.
    this.app.events.on(webphoneAppEvents.networkTestProgress, () => {
      const userId = ServiceModule.getCurrentUserId();
      if (!userId) {
        return;
      }
      const user = ServiceModule.getCurrentUser();
      if (!user) {
        return;
      }
      if (this.networkTestPreviousAvailabilityStatus) {
        return;
      }
      const current = ServiceModule.getAvailabilityStatus(user);
      this.networkTestUserId = userId;
      this.networkTestPreviousAvailabilityStatus = current;
      this.networkTestUnavailableUpdate =
        current === UserSetAvailabilityStatus.Unavailable
          ? Promise.resolve()
          : this.updateUserStatus(
              userId,
              UserSetAvailabilityStatus.Unavailable,
            );
    });

    this.app.events.on(
      webphoneAppEvents.networkTestResult,
      async (payload: NetworkTestResultPayload) => {
        const previousStatus = this.networkTestPreviousAvailabilityStatus;
        const userId = this.networkTestUserId;
        const unavailableUpdate = this.networkTestUnavailableUpdate;
        this.clearNetworkTestAvailabilityState();
        if (!payload.success) {
          return;
        }
        if (!previousStatus || !userId) {
          return;
        }
        await unavailableUpdate;
        if (previousStatus === UserSetAvailabilityStatus.Unavailable) {
          return;
        }
        void this.updateUserStatus(userId, previousStatus);
      },
    );
  }

  private clearNetworkTestAvailabilityState(): void {
    this.networkTestPreviousAvailabilityStatus = null;
    this.networkTestUserId = null;
    this.networkTestUnavailableUpdate = null;
  }

  /**
   * Updates the user status by calling the server mutation and updating local state.
   * Performs a complete update operation including server sync and Valtio state management.
   */
  public async updateUserStatusV2(
    userId: string,
    status: UserSetAvailabilityStatus,
  ) {
    try {
      this.app.logger.info('Setting user status V2', { userId, status });

      // Check if status update is already in progress
      const isLoading = ServiceModule.getUserStatusUpdateLoading(userId);
      if (isLoading) {
        this.app.logger.warn('Status update already in progress', {
          userId,
          status,
        });
        return;
      }

      // V2 implementation - enhanced logic for phase2 features
      await ServiceModule.updateUserStatus(userId, status);
    } catch (error) {
      console.error('Error updating user status V2:', error);
      // Handle specific error types with different toast messages
      let toastTitle: string = strings.toasts.setUserStatusFailure; // Default

      if (error instanceof Error) {
        if (error.name === CANCEL_WRAPUP_FAILED) {
          toastTitle = strings.toasts.cancelWrapupFailure;
        } else if (error.name === UPDATE_STATUS_FAILED) {
          toastTitle = strings.toasts.setUserStatusFailure;
        }
      }

      if (isApolloError(error)) {
        DiagnosticsModule.reportUserError({
          message: error.message,
          priority: 'direct',
        });
      }

      ToastModule.showToast({
        title: toastTitle,
        status: 'critical',
      });

      FullStoryModule.trackEvent('[CCP] User status update error V2', {
        userId,
        status,
        errorType: error instanceof Error ? error.name : 'unknown',
      });

      this.refetchUserStatus();
      // Don't throw - we've handled the error and shown the toast
    }
  }

  /**
   * Updates the user status by calling the server mutation and updating local state.
   * Performs a complete update operation including server sync and Valtio state management.
   */
  public async updateUserStatus(
    userId: string,
    status: UserSetAvailabilityStatus,
  ) {
    return this.updateUserStatusV2(userId, status);
  }

  /**
   * Handles automatic refresh when stuck status is detected.
   * Stuck status = user activity shows they're on a call but phone has no active call.
   */
  public async handleStuckStatus() {
    this.app.logger.warn('Auto-refreshing due to stuck status detection');
    await this.refreshUserStatus();
  }

  public async attemptRefreshUserStatus() {
    this.app.logger.info('Attempting to refresh user status', {
      userId: ServiceModule.getCurrentUserId(),
    });
    if (SoftPhoneModule.isInCall()) {
      this.app.logger.info('User in call');
      ModalsModule.openModal(ModalId.SimpleConfirmationModal, {
        title: strings.refreshConfirmationModal.title,
        body: strings.refreshConfirmationModal.body,
        isOpen: true,
        primaryText: strings.refreshConfirmationModal.primaryText,
        secondaryText: strings.refreshConfirmationModal.secondaryText,
        onPrimaryClick: () => {
          this.refreshUserStatus();
          ModalsModule.closeActiveModal();
        },
        onSecondaryClick: () => ModalsModule.closeActiveModal(),
        onClose: () => ModalsModule.closeActiveModal(),
      });
      return;
    }
    this.refreshUserStatus();
  }

  public async refreshUserStatus() {
    try {
      const userId = ServiceModule.getCurrentUserId();
      this.app.logger.info('Refreshing user status', {
        userId,
      });
      userStatusState.refreshUserStatusLoading = true;
      const { data } = await getClient().mutate({
        mutation: refreshUserStatusMutation,
      });
      if (!data || !data.refreshUserStatus.success) {
        throw new Error(
          'Refresh user status mutation returned false' + JSON.stringify(data),
        );
      } else {
        ToastModule.showToast({
          title: strings.toasts.refreshUserStatusSuccess,
          status: 'success',
        });
      }
      if (data?.refreshUserStatus.status.availabilityStatus) {
        const availabilityStatus = mapToUserSetAvailabilityStatus(
          data.refreshUserStatus.status.availabilityStatus,
        );

        if (!availabilityStatus) {
          this.app.logger.error('Unable to map availability status', {
            status: data.refreshUserStatus.status,
          });
          return;
        }

        if (userId) {
          ServiceModule.setUserStatus(userId, availabilityStatus);
        }
      }
    } catch (error) {
      const errorMessage =
        'Error refreshing user status: ' + extractErrorMessage(error);
      this.app.logger.error('Error refreshing user status', { error });
      ToastModule.showToast({
        title: strings.toasts.refreshUserStatusFailure,
        status: 'critical',
      });
      DiagnosticsModule.reportUserError({
        message: errorMessage,
        priority: 'direct',
      });
    } finally {
      userStatusState.refreshUserStatusLoading = false;
    }
  }

  private setRecoveryState(
    userId: string | null,
    recoveryEndsAt: number | null,
  ) {
    if (userId !== ServiceModule.getCurrentUserId() || !userId) {
      return;
    }

    userStatusState.recoveryTimeEndsAt = recoveryEndsAt;
  }

  private async refetchUserStatus() {
    const status = await getCurrentUserStatus();

    if (!status) {
      this.app.logger.error('Unable to map availability status', {
        status,
      });
      return;
    }
    ServiceModule.setCurrentUserStatus(status);
  }
}

export default new UserStatusModule(
  appManager.events,
  appManager.logger('UserStatus'),
);
